import { CommonModule, Location } from '@angular/common';
import { ChangeDetectorRef, Component, computed, inject, NgZone, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { debounceTime, forkJoin, map, merge, take } from 'rxjs';
import * as XLSX from 'xlsx';
import { MemberType, OngoingRelationship, SupplierFeedback, SupplierType } from '../../../models/enums';
import { Organisation } from '../../../models/organisation';
import { SupplierSummary } from '../../../models/supplier-summary';
import { ContactService } from '../../../services/contact.service';
import { InitiativeService } from '../../../services/initiative.service';
import { OrganisationService } from '../../../services/organisation.service';
import { ProjectService } from '../../../services/project.service';
import { ReferenceDataService } from '../../../services/reference-data.service';
import { TableStateService } from '../../../services/table-state.service';
import { iso3ToEmojiFlag } from '../../../utils/country-utils';
import { getGridTemplate, mapObject, MY_DATE_FORMATS } from '../../../utils/utils';
import { AdvancedSelectComponent, AdvancedSelectValue, createAdvancedSelectValue } from '../../misc/advanced-select/advanced-select.component';
import { ColumnSelectorDialogComponent } from '../../misc/column-selector-dialog/column-selector-dialog.component';
import { enumToSortedNestedOptions, mapToNestedOptions } from '../../misc/nested-options';

interface SupplierFilters {
  textFilter: string;
  // stakeholderGroups: AdvancedSelectValue;
  projects: AdvancedSelectValue;
  initiatives: AdvancedSelectValue;
  audiences: AdvancedSelectValue;
  products: AdvancedSelectValue;
  supplierTypes: AdvancedSelectValue;
  countries: AdvancedSelectValue;
}

@Component({
  selector: 'app-suppliers',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatSortModule,
    MatTableModule,
    MatToolbarModule,
    ReactiveFormsModule,
    MatIconModule,
    MatExpansionModule,
    MatChipsModule,
    MatDatepickerModule,
    MatTooltipModule,
    AdvancedSelectComponent,
    MatProgressSpinnerModule,
  ],
  providers: [
    provideLuxonDateAdapter(),
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'fr' },
  ],
  templateUrl: './suppliers.component.html',
  styleUrl: './suppliers.component.scss',
})
export class SuppliersComponent {
  SupplierType = SupplierType;
  SupplierFeedback = SupplierFeedback;
  OngoingRelationship = OngoingRelationship;

  tableId = 'suppliers'
  searchText = '';
  loading: boolean = false;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('filtersPanel') filtersPanel!: MatExpansionPanel;

  dataSource = new MatTableDataSource<SupplierSummary>();
  allColumns = [
    // { key: 'member', label: 'Member', type: 'icon' },
    { key: 'supplier', label: 'Supplier', type: 'icon' },
    { key: 'name', label: 'Name', type: 'longtext' },
    // { key: 'supplierFeedback', label: 'Feedback', type: 'icon' },
    { key: 'ongoingRelationship', label: 'Relationship', type: 'icon' },
    // { key: 'acronym', label: 'Full name', type: 'text' },
    // { key: 'stakeholderGroups', label: 'Stakeholder groups', type: 'chips' },
    { key: 'projects', label: 'Projects', type: 'chips' },
    { key: 'products', label: 'Products', type: 'chips' },
    { key: 'initiatives', label: 'Initiatives', type: 'chips' },
    // { key: 'topics', label: 'Topics of interest', type: 'chips' },
    // { key: 'targetAudiences', label: 'Target audiences', type: 'chips' },
    // { key: 'membershipType', label: 'Membership type', type: 'text' },
    // { key: 'lastModifiedOn', label: 'Last modified on', type: 'text' },
  ];
  columnLabelMap: Record<string, string> = {};

  defaultSelectedColumns: string[] = ['supplier', 'name', 'ongoingRelationship', 'projects', 'products'];
  selectedColumns: string[] = this.defaultSelectedColumns;
  pageEvent: PageEvent | undefined;

  filterForm: FormGroup;
  filters: SupplierFilters = this.getDefaultFilters();
  filtersExpanded: boolean = true;
  readonly showAllOrAny = signal(false);

  resetCounter = 0; // used to reset the filter form

  readonly refDataService = inject(ReferenceDataService);
  readonly organisationService = inject(OrganisationService);
  readonly contactService = inject(ContactService);
  readonly tableStateService = inject(TableStateService);
  readonly initiativeService = inject(InitiativeService);
  readonly projectService = inject(ProjectService);

  // readonly allStakeholderGroups = this.refDataService.getOrgStakeholderGroups();
  // readonly allStakeholderGroupOptions = computed(() => mapToNestedOptions(this.allStakeholderGroups(), true));
  readonly allProjects = toSignal(this.projectService.getAllProjects(), { initialValue: [] });
  readonly allProjectOptions = computed(() => mapToNestedOptions(this.allProjects(), true));
  readonly allProducts = this.refDataService.getProducts();
  readonly allProductOptions = computed(() => mapToNestedOptions(this.allProducts(), true));
  readonly allInitiatives = toSignal(this.initiativeService.getAllInitiatives(), { initialValue: [] });
  readonly allInitiativeOptions = computed(() => mapToNestedOptions(this.allInitiatives(), true))
  readonly allAudiences = this.refDataService.getAudiences();
  readonly allAudienceOptions = computed(() => mapToNestedOptions(this.allAudiences(), true));
  readonly allSupplierTypeOptions = [
    { label: "Contact", value: SupplierType.CONTACT, isGroup: false },
    { label: "Affiliated contact", value: SupplierType.CONTACT_IN_SUPPLIER_ORG, isGroup: false },
    { label: "Organisation", value: SupplierType.ORGANISATION, isGroup: false },
  ];
  readonly countries = this.refDataService.getCountries();
  readonly allCountryOptions = computed(() => this.countries().map(v =>
  ({
    value: v.isoCode,
    label: iso3ToEmojiFlag(v.isoCode) + ' ' + v.enName,
    isGroup: false,
    flag: iso3ToEmojiFlag(v.isoCode)
  })));

  /**
   * 
   * @param dialog 
   * @param snackBar 
   * @param router 
   * @param location 
   * @param cdr 
   * @param zone 
   * @param formBuilder 
   */
  constructor(private readonly dialog: MatDialog, private readonly snackBar: MatSnackBar, private readonly router: Router,
    private readonly location: Location, private readonly cdr: ChangeDetectorRef, private readonly zone: NgZone,
    private readonly formBuilder: FormBuilder) {
    this.filterForm = {} as FormGroup;
  }

  /**
   * 
   */
  ngOnInit(): void {
    this.columnLabelMap = Object.fromEntries(
      this.allColumns.map(col => [col.key, col.label])
    );
    this.loadSuppliers();

    // Display and clear any navigation message
    const state = this.location.getState() as { message?: string };
    if (state?.message) {
      this.snackBar.open(state.message, 'Close', {
        duration: 3000,
        panelClass: ['snackbar-success']
      });
      // Clear the message from history so it doesn't persist on reload
      history.replaceState({}, document.title, location.pathname);
    }

    this.filterForm = this.formBuilder.nonNullable.group<SupplierFilters>({
      textFilter: '',
      // stakeholderGroups: { selectedValues: [], matchAll: false },
      projects: { selectedValues: [], matchAll: false },
      initiatives: { selectedValues: [], matchAll: false },
      audiences: { selectedValues: [], matchAll: false },
      supplierTypes: { selectedValues: [], matchAll: false },
      products: { selectedValues: [], matchAll: false },
      countries: { selectedValues: [], matchAll: false },
    });
  }

  /**
   * 
   */

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.filterPredicate = this.filterFunction;
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'name':
          return item.sortingName.toLowerCase() ?? 'N/A'; // Ensure case-insensitive sorting
        // case 'acronym':
        //   return item.acronym?.toLowerCase() ?? 'N/A'; // Ensure case-insensitive sorting
        // case 'member':
        //   return (item.memberDetails ? 'true' : 'false');
        // case 'supplier':
        //   return (item.supplierDetails ? 'true' : 'false');
        // case 'membershipType':
        //   return item.memberDetails?.memberType ?? 'N/A';
        // case 'lastModifiedOn':
        //   return item.lastModifiedOn ?? 'N/A';
        default:
          return (item as Record<string, any>)[property];
      }
    }

    const saved = this.tableStateService.getState<SupplierFilters>(this.tableId);
    if (saved) {
      // console.debug('restoring', saved)
      this.zone.onStable.pipe(take(1)).subscribe(() => {
        this.paginator.pageIndex = saved.pageIndex;
        this.paginator.pageSize = saved.pageSize;
        this.sort.active = saved.sortActive;
        this.sort.direction = saved.sortDirection;
        this.filterForm.patchValue(saved.filters ? saved.filters : this.getDefaultFilters());
        this.filtersExpanded = saved.filtersExpanded;
        this.selectedColumns = saved.selectedColumnKeys ?? this.defaultSelectedColumns;
        if (saved.allColumnKeys) {
          this.allColumns.sort(
            (a, b) => saved.allColumnKeys.indexOf(a.key) - saved.allColumnKeys.indexOf(b.key)
          );
        }
        this.cdr.detectChanges();
      });
    }

    merge(
      this.filtersPanel.expandedChange,
      this.paginator.page,
      this.sort.sortChange,
      this.filterForm.valueChanges.pipe(debounceTime(300))
    ).subscribe(() => {
      this.saveState();
    });
    this.filterForm.valueChanges
      .pipe(debounceTime(300))
      .subscribe(filters => {
        if (filters)
          this.dataSource.filter = JSON.stringify(structuredClone(filters));
      });
  }


  openColumnSelector(): void {
    const dialogRef = this.dialog.open(ColumnSelectorDialogComponent, {
      data: {
        allColumns: this.allColumns,
        selected: [...this.selectedColumns],
        defaultSelection: [...this.defaultSelectedColumns]
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.selectedColumns = result.selected;
        this.allColumns = result.allColumns;
        this.saveState();
      }
    });
  }

  get displayedColumns(): string[] {
    return this.selectedColumns;
  }

  saveState() {
    const state = {
      pageIndex: this.paginator?.pageIndex ?? 0,
      pageSize: this.paginator?.pageSize ?? 10,
      sortActive: this.sort?.active,
      sortDirection: this.sort?.direction,
      filters: this.filterForm.getRawValue(),
      filtersExpanded: this.filtersExpanded,
      allColumnKeys: this.allColumns.map(c => (c.key)),
      selectedColumnKeys: this.selectedColumns
    }
    // console.debug('Saving state', state);
    this.tableStateService.setState(this.tableId, state);
  }

  goToDetail(supplier: SupplierSummary): void {
    this.saveState();
    if (supplier.supplierType === SupplierType.CONTACT || supplier.supplierType === SupplierType.CONTACT_IN_SUPPLIER_ORG) {
      this.router.navigate(['/contacts', supplier.id], {
        queryParams: { returnUrl: '/suppliers' }
      }).then(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    } else if (supplier.supplierType === SupplierType.ORGANISATION) {
      this.router.navigate(['/organisations', supplier.id], {
        queryParams: { returnUrl: '/suppliers' }
      }).then(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    } else {
      this.snackBar.open('Unknown supplier type', 'Close', {
        duration: 3000,
        panelClass: ['snackbar-error']
      });
    }
  }

  getDefaultFilters(): SupplierFilters {
    return {
      textFilter: '',
      // stakeholderGroups: createAdvancedSelectValue(),
      projects: createAdvancedSelectValue(),
      initiatives: createAdvancedSelectValue(),
      audiences: createAdvancedSelectValue(),
      products: createAdvancedSelectValue(),
      supplierTypes: createAdvancedSelectValue(),
      countries: createAdvancedSelectValue(),
    };
  }

  clearAllFilters() {
    console.debug('clear all filters')
    this.resetCounter++; // increment → triggers ngOnChanges in children
    this.filterForm.reset();
  }

  toggleFilterMatch(fieldName: keyof SupplierFilters) {
    this.filterForm.get(fieldName)?.setValue(!this.filterForm.get(fieldName)?.value);
  }

  togglePanel(event: MouseEvent) {
    event.stopPropagation(); // prevent header click from toggling
    this.filtersExpanded = !this.filtersExpanded;
  }

  get gridTemplate() {
    return getGridTemplate(this.selectedColumns, this.allColumns);
  }

  loadSuppliers(): void {
    this.loading = true;

    forkJoin([
      this.organisationService.getAllOrganisationSuppliers(),
      this.contactService.getAllContactSuppliers()
    ]).pipe(
      map(([orgs, contacts]) => [...orgs, ...contacts]),
      map(list => list.sort((a, b) => a.sortingName.localeCompare(b.sortingName)))
    ).subscribe({
      next: sortedList => {
        this.dataSource.data = sortedList;
        this.loading = false;
      },
      error: msg => {
        console.error('Failed to load suppliers:', msg);
        this.snackBar.open('Failed to load suppliers', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-error']
        });
        this.loading = false;
      }
    });
  }

  filterFunction = (data: SupplierSummary, filter: string) => {
    const rawFilters = JSON.parse(filter);
    const filters: SupplierFilters = {
      ...rawFilters,
    };

    // Name
    // Projects
    // Countries
    // Topics of interest
    // initiatives
    // Stakeholder group

    if (filters?.textFilter) {
      const searchStr = filters.textFilter.toLowerCase();
      if (!data.name?.toLowerCase().includes(searchStr)) {
        return false;
      }
    }

    if (filters?.supplierTypes?.selectedValues && filters.supplierTypes?.selectedValues.length > 0) {
      if (!filters.supplierTypes?.selectedValues.includes(data.supplierType)) {
        return false;
      }
    }
    // if (filters?.stakeholderGroups?.selectedValues && filters.stakeholderGroups?.selectedValues.length > 0) {
    //   const intersection = data.stakeholderGroups.filter(item => filters.stakeholderGroups?.selectedValues.includes(String(item.id)));
    //   if (filters.stakeholderGroups?.matchAll) {
    //     if (intersection.length < filters.stakeholderGroups?.selectedValues.length) {
    //       return false
    //     }
    //   } else if (intersection.length === 0) {
    //     return false;
    //   }
    // }
    if (filters?.projects?.selectedValues && filters.projects?.selectedValues.length > 0) {
      const intersection = data.projects.filter(item => filters.projects?.selectedValues.includes(String(item.id)));
      if (filters.projects?.matchAll) {
        if (intersection.length < filters.projects?.selectedValues.length) {
          return false
        }
      } else if (intersection.length === 0) {
        return false;
      }
    }
    if (filters?.initiatives?.selectedValues && filters.initiatives?.selectedValues.length > 0) {
      const intersection = data.initiatives.filter(item => filters.initiatives?.selectedValues.includes(String(item.id)));
      if (filters.initiatives?.matchAll) {
        if (intersection.length < filters.initiatives?.selectedValues.length) {
          return false
        }
      } else if (intersection.length === 0) {
        return false;
      }
    }
    if (filters?.products?.selectedValues && filters.products?.selectedValues.length > 0) {
      const intersection = data.products.filter(item => filters.products?.selectedValues.includes(String(item.id)));
      if (filters.products?.matchAll) {
        if (intersection.length < filters.products?.selectedValues.length) {
          return false
        }
      } else if (intersection.length === 0) {
        return false;
      }
    }


    return true;
  }

  export(): void {
    const data: any[] = [];
    this.dataSource.sortData(this.dataSource.filteredData, this.dataSource.sort!).forEach(d => {
      data.push(mapObject(d, this.selectedColumns));
    });

    const wb = XLSX.utils.book_new();
    wb.Props ??= {};
    wb.Props.Title = 'List of suppliers';

    const ws = XLSX.utils.json_to_sheet(data, { cellDates: true, dateNF: 'dd/mm/YYYY' });
    const range = XLSX.utils.decode_range(ws['!ref']!);
    let col_index = XLSX.utils.decode_col("W");
    for (let row = range.s.r + 1; row <= range.e.r; ++row) {
      const ref = XLSX.utils.encode_cell({ r: row, c: col_index })
      if (ws[ref]?.t === 'd') {
        ws[ref].z = 'mm/YYYY';
      }
    }
    XLSX.utils.book_append_sheet(wb, ws, "Suppliers");
    XLSX.writeFileXLSX(wb, "suppliers.xlsx");
  }

}
